import { Button, Icons, useToggle } from '@commenda/kakashi';
import {
  LegacyDocumentType,
  TaskParty,
  TaskStatus,
} from '@prisma/client';
import { ServiceRequestTaskLabel } from '../../../common/labels';
import { CorporationUploadFileModal } from '../../../common/modals/CorporationUploadFileModal';
import { ServiceRequestTaskIconConfig } from '../../../common/taskIconConfig';
import { useServiceRequestFile } from '../../../hook/useServiceRequestFile';
import { ServiceRequestTaskCard } from '../../../layout/components/taskCard/ServiceRequestTaskCard';
import { ServiceRequestTaskCardConfig, TCommonTaskProp } from '../../../types';
import { updateTaskStatus, viewUploadedFile } from '../../../utils';

export const UploadUnsignedLLPDeedTask = ({
  task,
  context,
  isTimelineView,
  displayNumber,
}: TCommonTaskProp) => {
  const { role, serviceRequest, mutate } = context;

  const uploadModalVisibility = useToggle();

  const [file, setFile] = useServiceRequestFile(
    serviceRequest,
    LegacyDocumentType.UnsignedLLPDeed
  );

  const handleViewFile = () => viewUploadedFile(file?.id);

  const config: ServiceRequestTaskCardConfig = {
    getLeftIcon: (status) => ServiceRequestTaskIconConfig[status],
    getStateConfig: (state) => {
      const stateMap = {
        [TaskParty.CLIENT]: {
          [TaskStatus.TO_DO]: {
            actions: null,
            label: (
              <ServiceRequestTaskLabel label="Agent uploads LLP Deed (including stamp paper)" />
            ),
          },
          [TaskStatus.COMPLETED]: {
            actions: (
              <Button size="sm" variant="secondary" onClick={handleViewFile}>
                View upload
              </Button>
            ),
            label: (
              <ServiceRequestTaskLabel
                label="LLP Deed (including stamp paper) uploaded by agent"
                timestamp={task.updatedAt}
                status={task.status}
              />
            ),
          },
        },
        [TaskParty.AGENT]: {
          [TaskStatus.TO_DO]: {
            actions: (
              <div>
                <Button
                  size="sm"
                  onClick={uploadModalVisibility.show}
                  variant="primary"
                >
                  <Icons.Upload01 className="size-4" />
                  Upload
                </Button>
              </div>
            ),
            label: (
              <ServiceRequestTaskLabel label="Upload LLP Deed (including stamp paper)" />
            ),
          },
          [TaskStatus.BACKLOG]: {
            actions: (
              <Button size="sm" disabled variant="primary">
                <Icons.Upload01 className="size-4" />
                Upload
              </Button>
            ),
            label: (
              <ServiceRequestTaskLabel label="Upload LLP Deed (including stamp paper)" />
            ),
          },
          [TaskStatus.COMPLETED]: {
            actions: (
              <Button size="sm" variant="secondary" onClick={handleViewFile}>
                View upload
              </Button>
            ),
            label: (
              <ServiceRequestTaskLabel
                label="LLP Deed (including stamp paper) uploaded"
                timestamp={task.updatedAt}
                status={task.status}
              />
            ),
          },
        },
      };

      return (
        stateMap[state.role]?.[state.status] ?? {
          label: task.title,
          actions: null,
        }
      );
    },
  };

  return (
    <div>
      <ServiceRequestTaskCard
        task={task}
        state={{
          role,
          status: task.status,
        }}
        config={config}
        context={context}
        isTimelineView={isTimelineView}
        displayNumber={displayNumber}
      />
      <CorporationUploadFileModal
        serviceRequest={serviceRequest}
        serviceRequestTaskId={task.id}
        file={file}
        setFile={setFile}
        isOpen={uploadModalVisibility.visible}
        setIsOpen={uploadModalVisibility.hide}
        onUploadSuccess={async () => {
          await updateTaskStatus({
            task,
            status: TaskStatus.COMPLETED,
            mutate,
          });
        }}
        documentType={LegacyDocumentType.UnsignedLLPDeed}
        heading="Upload LLP Deed"
      />
    </div>
  );
};
